import { useEffect, useMemo, useRef, useState } from 'react';

import { buildCallTranscript } from './call-transcript';
import {
  requestCallTranscript,
  type CallTranscriptResponse,
} from './validate-api';
import type { ValidateTurn } from './validate-runs';

/** Logs land seconds behind the call, so keep reading a while after it ends. */
const POLL_MS = 3_000;
const AFTER_END_MS = 60_000;

export interface CallTranscript {
  chatId: string | null;
  transcript: ValidateTurn[];
  problem: string | null;
  /** The call is over and the logs have had their minute to catch up. */
  done: boolean;
}

/**
 * A conversation read from the logs while it happens — a "Call me" call
 * (found by its voice channel) or a Pype Next widget chat (`knownChatId`) —
 * polled from when it starts until a minute after it ends.
 */
export const useCallTranscript = (args: {
  integratorId: string | null;
  /** A web chat's id, when the page already knows it. */
  knownChatId?: string | null;
  placedAt: number | undefined;
  live: boolean;
  nodeMessages: ReadonlyMap<number, string>;
}): CallTranscript => {
  const {
    integratorId,
    knownChatId = null,
    placedAt,
    live,
    nodeMessages,
  } = args;
  const [data, setData] = useState<CallTranscriptResponse | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const endedAt = useRef<number | null>(null);
  const chatId = useRef<string | null>(null);

  // A new call starts over.
  useEffect(() => {
    setData(null);
    setProblem(null);
    setDone(false);
    chatId.current = knownChatId;
    endedAt.current = null;
  }, [placedAt, integratorId, knownChatId]);

  useEffect(() => {
    if (live) endedAt.current = null;
    else if (placedAt && endedAt.current === null) endedAt.current = Date.now();
  }, [live, placedAt]);

  useEffect(() => {
    if ((!integratorId && !knownChatId) || !placedAt) return undefined;
    if (knownChatId) chatId.current = knownChatId;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const res = await requestCallTranscript({
          integratorId,
          sinceMs: placedAt,
          chatId: chatId.current,
        });
        if (stopped) return;
        if (res.chatId) chatId.current = res.chatId;
        setData(res);
        setProblem(null);
      } catch (err) {
        if (!stopped) {
          setProblem(err instanceof Error ? err.message : String(err));
        }
      }
      const finished =
        endedAt.current !== null && Date.now() - endedAt.current > AFTER_END_MS;
      if (stopped) return;
      if (finished) setDone(true);
      else timer = setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [integratorId, knownChatId, placedAt]);

  const transcript = useMemo(
    () =>
      data ? buildCallTranscript(data.requests, data.events, nodeMessages) : [],
    [data, nodeMessages]
  );
  return { chatId: data?.chatId ?? null, transcript, problem, done };
};
